import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Star, Search, CheckCircle2, MessageSquare, Filter, X } from 'lucide-react';

function StarsView({ rating }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={`stars-${rating}`}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          className="w-3.5 h-3.5"
          fill={n <= rating ? '#EAB308' : 'none'}
          stroke={n <= rating ? '#EAB308' : '#D1D5DB'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function formatDateShort(iso) {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return iso; }
}
function formatDateTime(iso) {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    date_from: '', date_to: '', min_rating: '', max_rating: '', resolved: '', search: '',
  });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '') params.append(k, v); });
    try {
      const [r, s] = await Promise.all([
        api.get(`/feedback?${params.toString()}`),
        api.get('/feedback/stats'),
      ]);
      setItems(r.data);
      setStats(s.data);
    } catch {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleResolved = async (item) => {
    try {
      const r = await api.put(`/feedback/${item.id}`, { resolved: !item.resolved });
      setItems(items.map(i => i.id === item.id ? { ...i, ...r.data } : i));
      if (selected?.id === item.id) setSelected({ ...selected, ...r.data });
      load();
    } catch { toast.error('Fehler'); }
  };

  const saveNote = async (item, note) => {
    try {
      const r = await api.put(`/feedback/${item.id}`, { internal_note: note });
      setItems(items.map(i => i.id === item.id ? { ...i, ...r.data } : i));
      if (selected?.id === item.id) setSelected({ ...selected, ...r.data });
      toast.success('Notiz gespeichert');
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl" data-testid="admin-feedback-page">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Nach dem Besuch</p>
        <h1 className="font-display text-3xl text-[#1C1C19]">Bewertungen</h1>
        <p className="text-sm text-[#5C5C59] mt-2 max-w-2xl">
          Übersicht aller Gäste-Bewertungen nach dem Restaurantbesuch.
        </p>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="feedback-stats">
          <Stat label="Bewertungen" value={stats.total_responses} testId="stat-total" />
          <Stat
            label="Durchschnitt"
            value={
              <span className="flex items-baseline gap-1">
                {stats.average_rating.toFixed(2)}
                <Star className="w-3.5 h-3.5" fill="#EAB308" stroke="#EAB308" />
              </span>
            }
            testId="stat-avg"
          />
          <Stat label="Offene" value={stats.unresolved} accent="#EF4444" testId="stat-unresolved" />
          <Stat label="Geplant/Versandt" value={stats.pending_requests} accent="#3B82F6" testId="stat-pending" />
          <div className="bg-white border border-[#E5E5E0] rounded p-3" data-testid="stat-distribution">
            <p className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-1">Verteilung</p>
            <div className="flex items-end gap-1 h-10">
              {[1, 2, 3, 4, 5].map(s => {
                const c = stats.distribution?.[String(s)] || 0;
                const max = Math.max(1, ...Object.values(stats.distribution || {}));
                const h = (c / max) * 100;
                return (
                  <div key={s} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full bg-[#EAB308]/30 rounded-t" style={{ height: `${h}%` }}></div>
                    <span className="text-[9px] text-[#5C5C59]">{s}★</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="feedback-filters">
        <div className="col-span-2">
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Suche</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#5C5C59]" />
            <input
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              data-testid="fb-search-input"
              className="w-full pl-8 pr-3 py-2 border border-[#E5E5E0] rounded text-sm"
              placeholder="Gast oder Text..."
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Besuch von</label>
          <input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="fb-from-input" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Besuch bis</label>
          <input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="fb-to-input" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Min. Sterne</label>
          <select value={filters.min_rating} onChange={e => setFilters({ ...filters, min_rating: e.target.value })} data-testid="fb-min-input" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
            <option value="">Alle</option>
            {[1, 2, 3, 4, 5].map(s => <option key={s} value={s}>{s}+</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Status</label>
          <select value={filters.resolved} onChange={e => setFilters({ ...filters, resolved: e.target.value })} data-testid="fb-resolved-input" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
            <option value="">Alle</option>
            <option value="false">Offen</option>
            <option value="true">Erledigt</option>
          </select>
        </div>
      </div>

      {items.length === 0 && !loading && (
        <div className="bg-white border border-dashed border-[#E5E5E0] rounded-lg p-12 text-center" data-testid="fb-empty">
          <MessageSquare className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
          <p className="text-[#5C5C59]">Noch keine Bewertungen.</p>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
        {items.map(item => {
          const lowRating = item.rating <= 2;
          return (
            <div
              key={item.id}
              onClick={() => setSelected(item)}
              data-testid={`fb-item-${item.id}`}
              className={`p-4 cursor-pointer hover:bg-[#F9F9F7] flex flex-col md:flex-row gap-3 items-start ${
                lowRating ? 'bg-[#FEE2E208]' : ''
              }`}
            >
              <div className="flex items-center gap-2 min-w-[140px]">
                <StarsView rating={item.rating} />
                {lowRating && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF4444]/15 text-[#EF4444] uppercase tracking-wider">Niedrig</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="font-medium text-[#1C1C19]">{item.customer_name || '–'}</p>
                  <p className="text-xs text-[#5C5C59]">
                    Besuch: {formatDateShort(item.request?.date)} · Absende: {formatDateTime(item.submitted_at)}
                  </p>
                </div>
                {item.comment && <p className="text-sm text-[#1C1C19] mt-1 line-clamp-2">{item.comment}</p>}
                {item.internal_note && (
                  <p className="text-xs text-[#5C5C59] mt-1 italic">📝 {item.internal_note}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleResolved(item); }}
                  data-testid={`fb-resolve-${item.id}`}
                  className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 border ${
                    item.resolved
                      ? 'bg-[#22C55E]/15 border-[#22C55E]/30 text-[#22C55E]'
                      : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {item.resolved ? 'Erledigt' : 'Erledigt markieren'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <FeedbackDetail
          item={selected}
          onClose={() => setSelected(null)}
          onSaveNote={(note) => saveNote(selected, note)}
          onToggleResolved={() => toggleResolved(selected)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent = '#1C1C19', testId }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-[#5C5C59]">{label}</p>
      <p className="text-2xl font-display mt-1" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function FeedbackDetail({ item, onClose, onSaveNote, onToggleResolved }) {
  const [note, setNote] = useState(item.internal_note || '');

  return (
    <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="fb-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Bewertung</p>
            <h2 className="font-display text-xl text-[#1C1C19]">{item.customer_name || '–'}</h2>
            {item.request?.date && <p className="text-xs text-[#5C5C59]">Besuch: {formatDateShort(item.request.date)}</p>}
          </div>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5 text-[#5C5C59]" /></button>
        </div>

        <div className="bg-[#F9F9F7] rounded p-4 mb-4">
          <div className="flex items-center gap-3">
            <StarsView rating={item.rating} />
            <span className="text-2xl font-display">{item.rating}/5</span>
          </div>
          {item.comment && (
            <p className="text-sm text-[#1C1C19] mt-3 leading-relaxed italic">„{item.comment}"</p>
          )}
        </div>

        <div className="mb-4">
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Interne Notiz</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            data-testid="fb-note-input"
            rows={3}
            className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
            placeholder="Notiz für Team, z.B. Maßnahmen, Antwort..."
          />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onToggleResolved} data-testid="fb-modal-resolve" className="px-3 py-2 border border-[#E5E5E0] rounded text-sm flex items-center gap-1 hover:border-[#1C1C19]">
            <CheckCircle2 className="w-4 h-4" /> {item.resolved ? 'Wieder offen' : 'Erledigt'}
          </button>
          <button onClick={() => onSaveNote(note)} data-testid="fb-modal-save-note" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm">
            Notiz speichern
          </button>
        </div>
      </div>
    </div>
  );
}
